import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import FaqSchema from "@/components/seo/FaqSchema";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";

import { regulatoryOfferingData } from "@/data/regulatoryOfferingData";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage } from "@/hooks/useLanguage";
import { getRAOfferingI18n } from "@/i18n/raOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";

interface FaqItem { q: string; a: string; }

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

/** Map Spanish URL slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "estrategia-de-regulatory-affairs-en-la-industria-farmacéutica": "regulatory-strategy-pharma",
  "desarrollo-de-fármacos": "drug-development",
  "desarrollo-en-fase-temprana": "early-development",
  "asesoramiento-científico": "scientific-advice",
  "procedimientos-regulatorios": "regulatory-procedures",
  "presentaciones-regulatorias": "submissions",
  "ectd": "ectd-publishing",
  "packaging-y-etiquetado": "artwork-labeling",
  "gestión-del-ciclo-de-vida": "lifecycle-management",
  "responsable-de-información": "information-officer",
  "enfermedades-raras": "orphan-diseases",
  "inteligencia-regulatoria": "regulatory-intelligence",
  "estrategia-reguladora-para-md-e-ivd": "regulatory-strategy-md-ivd",
  "diseño-y-desarrollo": "design-and-development",
  "documentación-técnica": "technical-documentation",
  "presentaciones-a-organismos-notificados": "notified-body-submissions",
  "vigilancia-poscomercialización": "post-market-surveillance",
  "servicios-de-representación-en-el-país": "in-country-representative-services",
  "persona-responsable-del-cumplimiento-de-la-normativa": "person-responsible-for-regulatory-compliance",
  "representante-autorizado-eu-rep": "authorized-representative-eu-rep",
  "representante-autorizado-de-suiza-ch-rep": "swiss-authorized-representative",
  "persona-responsable-del-reino-unido-ukrp": "ukrp-uk-responsible-person",
  "revisión-lingüística": "linguistic-review",
  "soporte-para-la-transición-de-ivdd-a-ivdr": "ivdr-transition-support",
  "cumplimiento-ivdr-certificacion-ce": "ivdr-compliance-ce-certification",
  "cumplimiento-mdr-certificacion-ce-para-dispositivos-medicos": "mdr-compliance-ce-certification-for-medical-devices",
  "apoyo-a-la-transición-de-mdd-a-mdr": "mdr-transition-support",
  "representante-legal": "legal-representative",
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  contentTags?: string[];
  contentTagGroups?: string[][];
  faqItems?: FaqItem[];
}> = {
  "/en/services/regulatory-affairs/regulatory-strategy-pharma": {
    title: "Regulatory Strategy for Pharma",
    subtitle: "Forming a concept to bring your pharmaceuticals to the market",
    description: "Establishing a regulatory strategy involves choosing the right procedure for a product type, scanning local regulations and requirements, choosing in which countries to launch and advising on the time to market.",
    contentKey: "regulatory-strategy-pharma",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/drug-development": {
    title: "Drug Development",
    subtitle: "From discovery to market",
    description: "Drug development entails drug discovery, pre-clinical and clinical testing. New chemical entities need to show minimum standards of safety, toxicity, pharmacokinetics and metabolism before they can be placed on the market.",
    contentKey: "drug-development",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/early-development": {
    title: "Early Development",
    subtitle: "Navigating the early stages of drug development",
    description: "An essential step in the early stages of developing a potential medicinal product is the preparation of the Clinical Trial Application for the First in Human clinical trial.",
    contentKey: "early-development",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/scientific-advice": {
    title: "Scientific Advice",
    subtitle: "Expert guidance for regulatory authority interactions",
    description: "Scientific advice procedures play a central role in the drug approval process. Our experienced consultants help you interact with all relevant health authorities.",
    contentKey: "scientific-advice",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/regulatory-procedures": {
    title: "Regulatory Procedures",
    subtitle: "Navigating EU & UK registration pathways",
    description: "There are various procedures available to register pharmaceutical drugs in the EU and UK. The method chosen depends on the type of drug and the intended route to market.",
    contentKey: "regulatory-procedures",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/submissions": {
    title: "Submissions",
    subtitle: "Getting your marketing authorization right the first time",
    description: "Navigating the regulatory landscape for marketing authorization can be complex, with strict requirements for documentation and compliance. We ensure your applications meet all regulatory standards.",
    contentKey: "submissions",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/ectd-publishing": {
    title: "eCTD Publishing",
    subtitle: "Electronic submissions done right",
    description: "Based on the content of the Common Technical Document format, the Electronic Common Technical Document transfers regulatory information from industry to agency in electronic form.",
    contentKey: "ectd-publishing",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/artwork-labeling": {
    title: "Artwork & Labeling",
    subtitle: "Marketing in compliance",
    description: "Directive 2001/83/EC requires that label texts must be in accordance with the approved Summary of Product Characteristics. QbD Group helps you create pharmaceutical artwork in full compliance.",
    contentKey: "artwork-labeling",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/lifecycle-management": {
    title: "Lifecycle Management",
    subtitle: "Managing changes throughout the product lifecycle",
    description: "Products that have been put on the market change over time and this process must be managed. QbD Group's experts accompany you throughout the whole lifecycle management process.",
    contentKey: "lifecycle-management",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/information-officer": {
    title: "Information Officer",
    subtitle: "Managing pharmaceutical information and marketing compliance",
    description: "To be fully compliant with legal requirements, a qualified expert has to take responsibility over how information on pharmaceuticals is communicated and marketed.",
    contentKey: "information-officer",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/orphan-diseases": {
    title: "Orphan Diseases",
    subtitle: "Supporting the development of medicines for rare diseases",
    description: "Orphan diseases are conditions that affect a small percentage of the population. QbD Group can support you in the development of medicines for patients suffering from rare diseases.",
    contentKey: "orphan-diseases",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/regulatory-intelligence": {
    title: "Regulatory Intelligence",
    subtitle: "Stay ahead of the regulatory landscape",
    description: "Regulatory intelligence is essential for successful product development and regulatory strategy. By leveraging up-to-date and comprehensive information, you can stay ahead of the competition.",
    contentKey: "regulatory-intelligence",
    contentTags: ["Regulatory Affairs"],
  },
  "/en/services/regulatory-affairs/regulatory-strategy-md-ivd": {
    title: "Regulatory Strategy for MD & IVD",
    subtitle: "A strategic approach to medical device and IVD market access",
    description: "A regulatory strategy is an essential framework that delineates the necessary steps and actions to effectively address the regulatory requirements for introducing a Medical Device or IVD to the market.",
    contentKey: "regulatory-strategy-md-ivd",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/design-and-development": {
    title: "Design & Development",
    subtitle: "Designing safe, effective medical devices from concept to market",
    description: "The design and development process is a core element of bringing safe and effective medical devices and IVDs to market. Our team provides expert guidance throughout the entire design lifecycle.",
    contentKey: "design-and-development",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/technical-documentation": {
    title: "Technical Documentation",
    subtitle: "Comprehensive regulatory documentation for MD & IVD",
    description: "The new regulations for IVDs and medical devices introduce more stringent requirements for technical documentation and clinical evidence. With over two decades of experience, we offer unparalleled expertise.",
    contentKey: "technical-documentation",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/notified-body-submissions": {
    title: "Notified Body Submissions",
    subtitle: "Your path to CE marking",
    description: "Navigating the submission process to a notified body is an essential step in obtaining CE marking for medical devices and in-vitro diagnostics. We offer comprehensive support tailored to your requirements.",
    contentKey: "notified-body-submissions",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/post-market-surveillance": {
    title: "Post-Market Surveillance",
    subtitle: "Ensuring ongoing safety and compliance",
    description: "Post-Market Surveillance is an essential requirement for ensuring the ongoing safety and efficacy of your IVD or medical device once it reaches the market.",
    contentKey: "post-market-surveillance",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/in-country-representative-services": {
    title: "In-Country Representative Services",
    subtitle: "EU-REP, UKRP, and CH-REP services",
    description: "Our regulatory experts serve as EU-REP/UKRP/CH-REP to allow easy access of IVD and MD devices to the European, UK, and Swiss markets.",
    contentKey: "in-country-representative-services",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/person-responsible-for-regulatory-compliance": {
    title: "Person Responsible for Regulatory Compliance (PRRC)",
    subtitle: "Mandatory EU MDR & IVDR requirement",
    description: "To sell products in the EU market, manufacturers of medical devices and IVDs must officially designate a PRRC. Without an officially designated PRRC, companies cannot market their products in the EU.",
    contentKey: "person-responsible-for-regulatory-compliance",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/authorized-representative-eu-rep": {
    title: "Authorized Representative (EU-REP)",
    subtitle: "Your EU-based regulatory partner",
    description: "If you're a medical device or IVD manufacturer outside the EU/EEA, appointing an EU-based Authorized Representative is a legal requirement to ensure compliance with MDR and IVDR.",
    contentKey: "authorized-representative-eu-rep",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/swiss-authorized-representative": {
    title: "Swiss Authorized Representative (CH-REP)",
    subtitle: "Access the Swiss medical device market",
    description: "Manufacturers based outside Switzerland must appoint a Swiss Authorized Representative to place medical devices and IVDs on the Swiss market.",
    contentKey: "swiss-authorized-representative",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/ukrp-uk-responsible-person": {
    title: "UK Responsible Person (UKRP)",
    subtitle: "Access the Great Britain medical device market",
    description: "Following Brexit, manufacturers located outside the UK must appoint a UK Responsible Person to place medical devices and IVDs on the Great Britain market.",
    contentKey: "ukrp-uk-responsible-person",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
  },
  "/en/services/regulatory-affairs/linguistic-review": {
    title: "Linguistic Review",
    subtitle: "Precise, compliant translations under tight deadlines",
    description: "One of the biggest challenges in the linguistic review process is the extremely short turnaround time for translations. QbD Group ensures high-quality translations that comply with procedural timelines.",
    contentKey: "linguistic-review",
    contentTags: ["Regulatory Affairs", "Pharma"],
  },
  "/en/services/regulatory-affairs/ivdr-transition-support": {
    title: "IVDR Transition Support",
    subtitle: "Navigate the IVDD to IVDR transition with confidence",
    description: "With average certification timelines ranging from 13–18 months and transition deadlines fast approaching, IVD manufacturers can't afford to wait. Planning early ensures compliance and gives you a competitive edge.",
    contentKey: "ivdr-transition-support",
    contentTags: ["Regulatory Affairs", "IVD"],
  },
  "/en/services/regulatory-affairs/ivdr-compliance-ce-certification": {
    title: "IVDR Compliance & CE Certification",
    subtitle: "Strategic IVDR compliance for secure and sustainable EU market access",
    description: "The EU In Vitro Diagnostic Regulation (EU) 2017/746 has fundamentally changed how in vitro diagnostics access the European market. QbD Group supports manufacturers with end-to-end IVDR compliance services, from regulatory roadmap to CE certification and long-term post-market compliance.",
    contentKey: "ivdr-compliance-ce-certification",
    contentTagGroups: [["Regulatory Affairs", "IVD"], ["IVDR"]],
    ogImage: "/images/offerings/ivdr-compliance-ce-certification-ra.jpg",
  },
  "/en/services/regulatory-affairs/mdr-compliance-ce-certification-for-medical-devices": {
    title: "MDR Compliance & CE Certification",
    subtitle: "Achieve and maintain MDR compliance for your medical devices",
    description: "The European Medical Device Regulation sets a higher bar for safety, quality, and transparency. Whether you are bringing a new device to market or transitioning an existing one, we guide every step.",
    contentKey: "mdr-compliance-ce-certification-for-medical-devices",
    contentTags: ["Regulatory Affairs", "Medical Devices"],
  },
  "/en/services/regulatory-affairs/mdr-transition-support": {
    title: "MDR Transition Support",
    subtitle: "Bridge the gap from MDD to MDR",
    description: "The shift from the MDD to the MDR is one of the most significant regulatory changes the medical device industry has faced in decades. QbD Group helps you navigate this transition.",
    contentKey: "mdr-transition-support",
    contentTags: ["Regulatory Affairs", "Medical Devices"],
  },
  "/en/services/regulatory-affairs/legal-representative": {
    title: "Legal Representative",
    subtitle: "EU representation for performance studies",
    description: "For sponsors of performance studies based outside the European Union, appointing a legal representative within the Union is a regulatory requirement. QbD Group provides expert EU representation.",
    contentKey: "legal-representative",
    contentTags: ["Regulatory Affairs", "IVD"],
  },
  "/en/services/regulatory-affairs/liaison-with-notified-bodies": {
    title: "Liaison with Notified Bodies",
    subtitle: "Strategic management of Notified Body interactions",
    description: "Engaging with a Notified Body is a key step in the conformity assessment process for medical devices and in vitro diagnostics. QbD Group supports manufacturers throughout Notified Body interactions, helping to ensure structured, clear, and strategy-aligned communication at each stage.",
    contentKey: "liaison-with-notified-bodies",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
    ogImage: "/images/offerings/liaison-notified-bodies-ra.jpg",
  },
  "/en/services/regulatory-affairs/materiovigilance": {
    title: "Vigilance",
    subtitle: "Incident monitoring and reporting for medical devices and IVDs",
    description: "Vigilance refers to the systematic monitoring, evaluation, and reporting of incidents related to medical devices and in vitro diagnostics after they are placed on the market. QbD Group supports manufacturers in establishing compliant and efficient vigilance systems.",
    contentKey: "materiovigilance",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
    ogImage: "/images/offerings/materiovigilance-ra.jpg",
  },
  "/en/services/regulatory-affairs/coaching-training": {
    title: "Coaching & Training",
    subtitle: "Build regulatory competence within your team",
    description: "QbD Group offers tailored training programs designed to build practical regulatory competence for medical device and IVD teams, from MDR/IVDR fundamentals to advanced technical documentation.",
    contentKey: "coaching-training",
    contentTags: ["Regulatory Affairs", "Medical Devices", "IVD"],
    ogImage: "/images/offerings/coaching-training-ra.jpg",
  },
};

const RegulatoryOffering = () => {
  const location = useStableLocation();
  const lang = useLanguage();
  const [formOpen, setFormOpen] = useState(false);

  // Resolve Spanish slugs to English content keys
  let page = offeringPages[location.pathname];
  let resolvedContentKey: string | undefined;

  // Localized routes that keep the English slug (e.g. /zh-cn/services/...) map
  // onto the English content key by swapping the language prefix.
  if (!page) {
    const enEquivalent = location.pathname.replace(/^\/(?:zh-cn|es)\//, "en/").replace(/^en\//, "/en/");
    page = offeringPages[enEquivalent];
  }

  if (!page && lang === "es") {
    const pathParts = location.pathname.split("/");
    const esSlug = pathParts[pathParts.length - 1];
    const enKey = lookupSlug(ES_SLUG_MAP, esSlug);
    if (enKey) {
      // Find the English page by contentKey
      const enEntry = Object.entries(offeringPages).find(([, v]) => v.contentKey === enKey);
      if (enEntry) {
        page = enEntry[1];
        resolvedContentKey = enKey;
      }
    }
  }

  const parentService = getServiceBySlug("regulatory-affairs");

  if (!page || !parentService) return <Navigate to="/en/services/regulatory-affairs" replace />;

  // Get i18n overrides
  const contentKey = resolvedContentKey || page.contentKey;
  const i18n = getRAOfferingI18n(contentKey, lang);
  const serviceI18n = getServiceContent("regulatory-affairs", lang);

  const displayTitle = i18n?.title || page.title;
  const displaySubtitle = i18n?.subtitle || page.subtitle;
  const displayDescription = i18n?.description || page.description;

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
    ...(page.ogImage ? { heroImage: page.ogImage } : {}),
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = `${displayTitle} — Regulatory Affairs | QbD Group`;
  const metaDesc = displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Regulatory Affairs"];

  const data = regulatoryOfferingData[contentKey];

  // Merge i18n into data for display
  const displayData = data && i18n ? {
    ...data,
    whatTitle: i18n.whatTitle,
    whatParagraphs: i18n.whatParagraphs,
    whyTitle: i18n.whyTitle,
    whyIntro: i18n.whyIntro,
    whyBenefits: data.whyBenefits?.map((b, idx) => ({
      ...b,
      title: i18n.whyBenefits[idx]?.title || b.title,
      text: i18n.whyBenefits[idx]?.text || b.text,
    })),
    partnerTitle: i18n.partnerTitle,
    partnerIntro: i18n.partnerIntro,
    partnerBullets: i18n.partnerBullets,
    ...(i18n.extraSections ? {
      extraSections: data.extraSections?.map((s, idx) => ({
        ...s,
        title: i18n.extraSections?.[idx]?.title || s.title,
        paragraphs: i18n.extraSections?.[idx]?.paragraphs || s.paragraphs,
      })),
    } : {}),
    ...(i18n.helpCards && data.helpCards ? {
      helpCards: {
        ...data.helpCards,
        heading: i18n.helpCards.heading,
        intro: i18n.helpCards.intro,
        cards: data.helpCards.cards.map((c, idx) => ({
          ...c,
          title: i18n.helpCards!.cards[idx]?.title || c.title,
          text: i18n.helpCards!.cards[idx]?.text || c.text,
        })),
      },
    } : {}),
    ...(i18n.processTitle ? { processTitle: i18n.processTitle } : {}),
    ...(i18n.processIntro ? { processIntro: i18n.processIntro } : {}),
    ...(i18n.processSteps && data.processSteps ? {
      processSteps: data.processSteps.map((s, idx) => ({
        ...s,
        title: i18n.processSteps?.[idx]?.title || s.title,
        text: i18n.processSteps?.[idx]?.text || s.text,
      })),
    } : {}),
  } : data;

  // Determine breadcrumb parent label based on path
  const isLandingInEurope = location.pathname.includes("/industries/pharma-biotech/");
  const isEs = lang === "es";
  const parentCrumbs = isLandingInEurope
    ? [{ label: "Pharma & Biotech", to: "/en/industries/pharma-biotech" }]
    : [{ label: "Regulatory Affairs", to: isEs ? "/es/servicios/regulatory-affairs" : "/en/services/regulatory-affairs" }];
  const baseCrumb = isLandingInEurope
    ? { label: "Industries", to: "/en/industries" }
    : { label: isEs ? "Servicios" : "Services", to: localizeHref("/en/services", lang) };

  return (
    <>
      <SEOHead title={metaTitle} description={metaDesc} canonical={pageUrl} ogImage={pageOgImage}>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "Service", name: page.title, description: page.description, provider: { "@type": "Organization", name: "QbD Group", url: SITE }, url: pageUrl, isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` }, ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)) })}
        </script>
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: baseCrumb.label, path: baseCrumb.to },
          { name: parentCrumbs[0].label, path: parentCrumbs[0].to },
          { name: page.title },
        ]}
      />

      <ScrollToTop />
      <Navbar logoOverride={DIVISION_LOGOS["regulatory-affairs"]} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton
          baseCrumb={baseCrumb}
          parentCrumbs={parentCrumbs}
        />

        {displayData && <GenericOfferingContent data={displayData} onCtaClick={() => setFormOpen(true)} hidePartnerSection />}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={displayData ? { intro: displayData.partnerIntro, bullets: displayData.partnerBullets } : undefined}
        />

        {page.faqItems && page.faqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <FaqSchema items={page.faqItems.map((faq) => ({ question: faq.q, answer: faq.a }))} />
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Submission planning requirements", es: "Expedientes y cumplimiento", "zh-cn": "申报规划法规合规" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">{lang === "es" ? "Preguntas frecuentes" : lang === "zh-cn" ? "常见问题" : "Frequently Asked Questions"}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {page.faqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} contentTagGroups={page.contentTagGroups} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{parentService.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={parentService.formHeading}
              subtitle={parentService.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={parentService.formHeading}
          description={parentService.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default RegulatoryOffering;
